const initialState = {
  loading: false,
  activities: [],
  overdueActivities: [],
  upcomingActivities: [],
  editComment: { id: '', comment: '' },
  deleteCommentObj: { deleteCommentId: '', isDeleteComment: false },

  error: null,
  filters: { type: 'all', status: 'all', assignedTo: 'me', dateRange: 'today', priority: 'all', page: 1, limit: 20, sort: '-createdAt' },

  selectedActivity: null,
  pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 20 },
  statistics: { total: 0, completed: 0, pending: 0, inProgress: 0, overdue: 0, completionRate: 0, typeBreakdown: {}, priorityBreakdown: {} },
  activityModal: { isOpen: false, type: 'task', data: null },
  activityForm: {
    // Common fields
    valueRichText: '',

    // Task fields
    taskTitle: '',
    taskType: 'to-do',
    priority: 'medium',
    assignee: {},
    dueDate: '',
    dueTime: '12:00',
    reminderType: 'email',
    reminderDate: '',
    isRecurring: false,
    queue: 'none',

    // Note fields
    followUpDate: '',
    createFollowUp: false,
    followUpTaskType: 'to-do',
    
    // Attachments
    attachments: [], // Array of attachment objects
    pendingAttachments: [] // Files waiting to be uploaded
  },
  dropdowns: {
    showMore: false,
    showAssociated: false,
    dropdownOpenTODO: false,
    dropdownOpenDays: false,
    dropdownOpenQueue: false,
    dropdownOpenClient: false,
    dropdownOpenDaysTask: false,
    dropdownOpenPriority: false,
  },

  associations: {}, // Dynamic associations: { deals: [], contacts: [], companies: [], ... }
  activeActivityBox: null, // null | 'note' | 'task' | 'schedule' | 'mail' | 'call' | 'sms'
  activitySource: null, // Source of activity creation: 'pipeline' | 'viewModal' | 'sidebar' | null
  selectedContactEmail: null, // Email of selected contact for emails
  objectData: null, // Object data (deal/contact/company) for activity context
  emailReplyData: null, // Pre-fill data for reply/forward: { mode, to, subject, body, threadId, inReplyTo }
  
  // Attachments
  attachments: {
    uploading: false,
    uploadProgress: {}, // { [fileId]: percentage }
    tempFiles: [], // Files being uploaded
    errors: {}, // Upload errors by fileId
    activityAttachments: {} // { [activityId]: attachments[] }
  },

  // Unified Conversation State (SMS, WhatsApp, Email)
  conversation: {
    loading: false,
    sending: false,
    grouped: { sms: [], whatsapp: [], email: [] },
    timeline: [],
    counts: { sms: 0, whatsapp: 0, email: 0, total: 0 },
    call: { agents: [], phones: [], loading: false }
  },

  // WhatsApp Templates
  whatsappTemplates: {
    templates: [],
    loading: false,
    error: null
  }
};

const ActivitiesReducer = (state = initialState, action) => {
  switch (action['type']) {
    case 'CREATE_ACTIVITY':
      return { ...state, loading: true, error: null };

    case 'SET_ACTIVITY':
      return { ...state, activities: action['payload'], loading: false, error: null };

    case 'SET_SINGLE_ACTIVITY':
      return { ...state, selectedActivity: action['payload'], loading: false, error: null };

    case 'SET_ACTIVITY_LOADING':
      return { ...state, loading: action['payload'] };

    case 'SET_ACTIVITY_ERROR':
      return { ...state, error: action['payload'], loading: false };

    case 'TOGGLE_ACTIVITY_MODAL':
      return {
        ...state,
        activityModal: {
          isOpen: action['payload']['isOpen'] !== undefined ? action['payload']['isOpen'] : !state['activityModal']['isOpen'],
          type: action['payload']['type'] || state['activityModal']['type'],
          data: action['payload']['data'] !== undefined ? action['payload']['data'] : state['activityModal']['data']
        }
      };

    case 'SET_ACTIVITY_TYPE':
      return { ...state, activityModal: { ...state['activityModal'], type: action['payload'] } };

    case 'UPDATE_ACTIVITY_FORM_FIELD':
      return {
        ...state,
        activityForm: { ...state['activityForm'], [action['payload']['field']]: action['payload']['value'] }
      };

    case 'RESET_ACTIVITY_FORM':
      return {
        ...state,
        activeActivityBox: null,
        selectedContactEmail: null,
        objectData: null,
        emailReplyData: null,
        dropdowns: initialState.dropdowns,
        activityForm: initialState.activityForm,
      };

    case 'TOGGLE_DROPDOWN':
      return {
        ...state,
        dropdowns: { ...state['dropdowns'], [action['payload']]: !state['dropdowns'][action['payload']] }
      };

    case 'SET_ACTIVE_ACTIVITY_BOX':
      // If sourceId is 'related', merge associations instead of replacing
      const newAssociations = action['payload']?.['sourceId'] === 'related'
        ? { ...state['associations'], ...action['payload']?.['associations'] }
        : action['payload']?.['associations'] || {};

      return {
        ...state,
        associations: newAssociations,
        activeActivityBox: action['payload']?.['activityBox'],
        activitySource: action['payload']?.['source'] || state['activitySource'],
        selectedContactEmail: action['payload']?.['selectedContactEmail'] || null,
        objectData: action['payload']?.['objectData'] || null,
        emailReplyData: action['payload']?.['emailReplyData'] || null
      };

    case 'SET_EDIT_COMMENT':
      return {
        ...state,
        editComment: { ...state['editComment'], id: action['payload']['id'], comment: action['payload']['comment'] }
      };

    case 'SET_DELETE_COMMENT':
      return {
        ...state,
        deleteCommentObj: { ...state['deleteCommentObj'], deleteCommentId: action['payload']['deleteCommentId'], isDeleteComment: action['payload']['isDeleteComment'] }
      };
      
    // Attachment cases
    case 'ADD_PENDING_ATTACHMENT':
      return {
        ...state,
        activityForm: { ...state['activityForm'], pendingAttachments: [...state['activityForm']['pendingAttachments'], action['payload']] }
      };

    case 'UPLOAD_ATTACHMENT':
      return {
        ...state,
        attachments: { ...state['attachments'], uploading: true, tempFiles: [...state['attachments']['tempFiles'], action['payload']['file']] }
      };

    case 'SET_UPLOAD_PROGRESS':
      return {
        ...state,
        attachments: { ...state['attachments'], uploadProgress: { ...state['attachments']['uploadProgress'], [action['payload']['fileId']]: action['payload']['progress'] } }
      };

    case 'UPLOAD_ATTACHMENT_SUCCESS':
      return {
        ...state,
        attachments: {
          ...state['attachments'],
          uploading: false,
          tempFiles: state['attachments']['tempFiles'].filter(f => f['id'] !== action['payload']['fileId']),
          uploadProgress: { ...state['attachments']['uploadProgress'], [action['payload']['fileId']]: undefined }
        },
        activityForm: {
          ...state['activityForm'],
          attachments: [...state['activityForm']['attachments'], action['payload']['attachment']],
          pendingAttachments: state['activityForm']['pendingAttachments'].filter(f => f['id'] !== action['payload']['fileId'])
        }
      };

    case 'UPLOAD_ATTACHMENT_FAILURE':
      return {
        ...state,
        attachments: {
          ...state['attachments'],
          uploading: false,
          errors: { ...state['attachments']['errors'], [action['payload']['fileId']]: action['payload']['error'] },
          tempFiles: state['attachments']['tempFiles'].filter(f => f['id'] !== action['payload']['fileId']),
          uploadProgress: { ...state['attachments']['uploadProgress'], [action['payload']['fileId']]: undefined }
        }
      };

    case 'REMOVE_ATTACHMENT':
      return {
        ...state,
        activityForm: { ...state['activityForm'], attachments: state['activityForm']['attachments'].filter(a => a['_id'] !== action['payload']) }
      };

    case 'SET_ACTIVITY_ATTACHMENTS':
      return {
        ...state,
        attachments: { ...state['attachments'], activityAttachments: { ...state['attachments']['activityAttachments'], [action['payload']['activityId']]: action['payload']['attachments'] } }
      };

    case 'CLEAR_PENDING_ATTACHMENTS':
      return {
        ...state,
        activityForm: { ...state['activityForm'], pendingAttachments: [] },
        pendingAttachments: []
      };

    case 'REMOVE_PENDING_ATTACHMENT':
      return {
        ...state,
        activityForm: {
          ...state['activityForm'],
          pendingAttachments: state['activityForm']['pendingAttachments'].filter(f => f['id'] !== action['payload'])
        }
      };

    // Unified Conversation Cases (SMS, WhatsApp, Email)
    case 'SET_CONVERSATION_LOADING':
      return { ...state, conversation: { ...state['conversation'], loading: action['payload'] } };

    case 'SET_CONVERSATION_SENDING':
      return { ...state, conversation: { ...state['conversation'], sending: action['payload'] } };

    case 'SET_CONVERSATION_HISTORY':
      return { ...state, conversation: { ...state['conversation'], grouped: action['payload']['grouped'], timeline: action['payload']['timeline'], counts: action['payload']['counts'], loading: false } };

    case 'ADD_CONVERSATION_MESSAGE':
      const channel = action['payload']['channel'];
      const newMessage = action['payload']['message'];
      return { ...state, conversation: { ...state['conversation'], grouped: { ...state['conversation']['grouped'], [channel]: [...state['conversation']['grouped'][channel], newMessage] }, timeline: [...state['conversation']['timeline'], newMessage].sort((a, b) => new Date(a['timestamp']) - new Date(b['timestamp'])), counts: { ...state['conversation']['counts'], [channel]: state['conversation']['counts'][channel] + 1, total: state['conversation']['counts']['total'] + 1 }, sending: false } };

    case 'SET_CONVERSATION_RESOURCES':
      return { ...state, conversation: { ...state['conversation'], call: { ...state['conversation']['call'], agents: action['payload']['agents'] || [], phones: action['payload']['phones'] || [], loading: false } } };

    // WhatsApp Templates
    case 'GET_WHATSAPP_TEMPLATES':
      return { ...state, whatsappTemplates: { ...state['whatsappTemplates'], loading: true, error: null } };

    case 'SET_WHATSAPP_TEMPLATES':
      return { ...state, whatsappTemplates: { templates: action['payload']['templates'] || [], loading: action['payload']['loading'] ?? false, error: action['payload']['error'] || null } };

    default:
      return state;
  }
};

export default ActivitiesReducer;